import React, { useEffect, useState } from 'react';
import { Plus, Copy, Trash2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { fecha, Badge, Empty } from './ui';

const EJEMPLO = `curl -X POST https://admin.udream.app/api/ingest/reportes \\
  -H "Authorization: Bearer TU_LLAVE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "cliente": "Grupo Velsky",
    "servicio": "Paid Media",
    "periodo": "2026-09",
    "conclusion": "Bajamos el costo por consulta un 18% respecto de agosto.",
    "plataformas": [
      { "nombre": "Google Ads", "inversion": 350000, "impresiones": 45000, "clics": 1240, "resultados": 45 },
      { "nombre": "Meta Ads", "inversion": 200000, "impresiones": 82000, "clics": 900, "resultados": 30 }
    ],
    "destacados": [{ "label": "Consultas por WhatsApp", "valor": 75, "variacion": "+18%" }],
    "publicar": "portal"
  }'`;

export function LlavesApi() {
  const [tokens, setTokens] = useState(null);
  const [nuevo, setNuevo] = useState(null);

  const cargar = () => api.get('/agency/reportes-campana/tokens/lista').then(r => setTokens(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    const nombre = window.prompt('¿Cómo querés llamar a esta llave?', 'Agente de reportes');
    if (!nombre) return;
    try {
      const { data } = await api.post('/agency/reportes-campana/tokens', { nombre });
      setNuevo(data.token); cargar();
    } catch { toast.error('No se pudo crear'); }
  };
  const bajar = async (t) => {
    if (!window.confirm(`Dar de baja "${t.nombre}"? El agente que la use va a dejar de poder cargar reportes.`)) return;
    try { await api.delete('/agency/reportes-campana/tokens/' + t.id); cargar(); } catch { toast.error('Error'); }
  };
  const copiar = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success('Copiado')).catch(() => {});

  if (!tokens) return <div className="card" style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><KeyRound size={18} /> Llaves de API</h3>
        <button className="btn btn-secondary btn-sm" onClick={crear}><Plus size={14} /> Nueva llave</button>
      </div>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Con una llave, el agente de reportes de tu Mac puede subir el informe del mes y sus números directo al portal del cliente.
          La llave se muestra una sola vez: guardala en el proyecto del agente.
        </div>

        {nuevo && (
          <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 700, marginBottom: 6 }}>Copiala ahora, no se vuelve a mostrar</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{nuevo}</code>
              <button className="btn btn-secondary btn-sm" onClick={() => copiar(nuevo)}><Copy size={14} /> Copiar</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setNuevo(null)}>Listo</button>
            </div>
          </div>
        )}

        {tokens.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{tokens.map(t => (
              <tr key={t.id} style={{ opacity: t.activo ? 1 : 0.5 }}>
                <td style={{ ...td, fontWeight: 600 }}>{t.nombre}</td>
                <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{t.prefijo}…</td>
                <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>
                  {t.ultimo_uso ? `usada el ${fecha(t.ultimo_uso)}` : 'sin usar'}</td>
                <td style={td}>{t.activo ? <Badge label="Activa" color="#22c55e" bg="rgba(34,197,94,0.15)" /> : <Badge label="De baja" />}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {t.activo ? <button className="btn btn-ghost btn-sm" onClick={() => bajar(t)}><Trash2 size={14} /></button> : null}
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : <Empty>Todavía no creaste ninguna llave</Empty>}
      </div>
    </div>
  );
}

/* Documentación corta de los endpoints, para tener a mano */
export function ApiDocs() {
  const pre = { background: 'var(--bg-secondary)', padding: 14, borderRadius: 10, fontSize: 12, overflowX: 'auto',
    whiteSpace: 'pre', color: 'var(--text-secondary)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
  return (
    <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cómo la usa el agente</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Tres endpoints, todos con la llave en el encabezado. Si el reporte de ese mes ya existe, se actualiza en vez de duplicarse.
        </div>
        <div style={{ fontSize: 13, marginBottom: 8 }}><b>1.</b> <code>GET /api/ingest/clientes</code> — la lista de clientes con sus servicios y sus ids.</div>
        <div style={{ fontSize: 13, marginBottom: 8 }}><b>2.</b> <code>GET /api/ingest/reportes?cliente=Grupo Velsky</code> — los meses anteriores, para comparar.</div>
        <div style={{ fontSize: 13, marginBottom: 12 }}><b>3.</b> <code>POST /api/ingest/reportes</code> — carga o actualiza el mes:</div>
        <pre style={pre}>{EJEMPLO}</pre>
        <div style={{ fontSize: 13, margin: '14px 0 8px' }}>
          Y para adjuntar el informe, con el id que devuelve el paso anterior:
        </div>
        <pre style={pre}>{`curl -X POST https://admin.udream.app/api/ingest/reportes/ID/documento \\
  -H "Authorization: Bearer TU_LLAVE" \\
  -F "documento=@reporte-septiembre.pdf"`}</pre>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14 }}>
          <b>publicar</b> acepta <code>no</code>, <code>portal</code>, <code>email</code> o <code>ambos</code>. Si lo dejás en <code>no</code>,
          el reporte queda como borrador y lo publicás vos desde la ficha del servicio.
        </div>
    </div>
  );
}
